import { inject } from '@angular/core';
import {
  OAuthErrorEvent,
  OAuthService,
  OAuthStorage,
} from 'angular-oauth2-oidc';
import * as Sentry from '@sentry/angular';
import { environment } from '@et/environment';
import {
  TokenRefreshCoordinator,
  TokenRefreshUnavailableError,
  useBetterAuthForCurrentPlatform,
} from '@et/guards';
import type { EscrowtabOidcApp } from '../injection/escrowtab-oidc-app.token';
import {
  betterAuthDiscoveryUrl,
  buildEscrowtabAuthConfig,
} from './escrowtab-oauth.config';

/**
 * Key behind `preserveRequestedRoute`. The library restores it after the code exchange
 * whether or not that flag is still on, so a value a previous build persisted would keep
 * rewriting `/auth/callback` to a stale page (e.g. `/auth/forgot-password`) and strand
 * users there after a successful sign-in. Clear it once on boot.
 */
const LIBRARY_REQUESTED_ROUTE_KEY = 'requested_route';

/**
 * `OAuthService.tryLoginCodeFlow()` runs unconditionally on every app bootstrap
 * and — unless there's no `state` in the URL at all — immediately strips
 * `code=`, `scope=`, `state=`, and `session_state=` from the address bar via
 * `history.replaceState()` (see angular-oauth2-oidc's `tryLoginCodeFlow`),
 * *before* Angular Router's `ActivatedRoute.queryParams` is ever read. That's
 * correct for a real `/auth/callback` (it has a `code`/`error` to consume),
 * but the issuer's OIDC provider also redirects unauthenticated `/authorize`
 * requests straight to `/auth/login?response_type=code&...&state=...` so the
 * SPA can collect credentials before resuming — this is a *request* being
 * forwarded, not a callback, and it has no `code`/`error` yet. Auto-running
 * `tryLoginCodeFlow()` there destroys `state`/`scope` before
 * `LoginComponent.ngOnInit()` can capture them into `pendingAuthorizeQuery`,
 * so the eventual resume to `/authorize` is missing `state` and the issuer
 * echoes back `state=undefined` on the final redirect — surfacing as
 * `invalid_nonce_in_state` in the native app after the system browser closes.
 * Skip the auto `tryLogin()` for this one shape of URL; `LoginComponent`
 * handles it explicitly via `pendingAuthorizeQuery`/`buildOidcAuthorizeResumeUrl`.
 */
function isPendingOidcAuthorizeRequestUrl(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  return (
    params.get('response_type') === 'code' &&
    !!params.get('client_id') &&
    !!params.get('redirect_uri') &&
    !params.get('code') &&
    !params.get('error')
  );
}

/**
 * `setupAutomaticSilentRefresh()` schedules a single far-future `setTimeout` for the
 * token's remaining lifetime. Browsers throttle/suspend timers in backgrounded tabs, and
 * OS sleep pauses the JS event loop entirely, so that timer can fire well after the token
 * (and possibly the refresh token) has already expired. Re-checking on `visibilitychange`/
 * `focus` recovers as soon as the user actually returns, instead of waiting on a timer that
 * may never fire on time.
 */
function registerActivityBasedRefresh(
  coordinator: TokenRefreshCoordinator,
  onRecovered: () => void,
) {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }
  const onActivity = () => {
    if (document.visibilityState !== 'visible') {
      return;
    }
    coordinator.ensureValidToken().then(
      (refreshed) => {
        if (!refreshed) {
          coordinator.forceReauth(
            window.location.pathname + window.location.search,
          );
          return;
        }
        onRecovered();
      },
      (error: unknown) => {
        // Issuer unreachable (wifi not back after sleep, 5xx): the session is intact.
        // Stay put — the next focus / `online` event retries.
        if (!(error instanceof TokenRefreshUnavailableError)) {
          Sentry.captureException(error, {
            tags: { area: 'oidc-activity-refresh' },
          });
        }
      },
    );
  };
  document.addEventListener('visibilitychange', onActivity);
  window.addEventListener('focus', onActivity);
  // A kiosk that stays focused while the network drops never fires focus/visibility
  // again, so also retry the moment connectivity returns.
  window.addEventListener('online', onActivity);
}

export function escrowtabOAuthInitializerFactory(
  oauthService: OAuthService,
  app: EscrowtabOidcApp,
  storage: OAuthStorage,
): () => Promise<unknown> {
  const coordinator = inject(TokenRefreshCoordinator);

  return () => {
    if (!useBetterAuthForCurrentPlatform()) {
      return Promise.resolve(undefined);
    }
    const config = buildEscrowtabAuthConfig(environment, app);
    oauthService.configure(config);
    storage.removeItem(LIBRARY_REQUESTED_ROUTE_KEY);
    console.log('[oidc] initializer starting', {
      app,
      issuer: config.issuer,
      clientId: config.clientId,
      redirectUri: config.redirectUri,
      requireHttps: config.requireHttps,
    });

    oauthService.events.subscribe((event) => {
      // Log every OIDC event (not just errors) while diagnosing the native
      // "stuck on Signing you in" issue — this is the only place that sees
      // `discovery_document_loaded` / `code_error` / `token_error` etc.
      console.log('[oidc] event', event.type, event);
      if (event instanceof OAuthErrorEvent) {
        const reason =
          event.reason instanceof Error
            ? event.reason
            : new Error(String(event.reason ?? event.type));
        console.error('[oidc] OAuthErrorEvent', event.type, event.reason);
        Sentry.captureException(reason, {
          tags: { area: 'oidc-oauth-event', type: event.type },
        });
      }
      /**
       * `setupAutomaticSilentRefresh()` calls `refreshToken()` directly (not via
       * TokenRefreshCoordinator). Better Auth's refresh response can omit
       * `id_token`, so the library still emits `token_refreshed` while the SPA's
       * bearer JWT stays expired — and the access-token timer reschedules another
       * refresh. Abandon that unusable session instead of looping.
       */
      if (event.type === 'token_refreshed' && !oauthService.hasValidIdToken()) {
        console.error(
          '[oidc] token_refreshed without a valid id_token — abandoning session to stop refresh loop',
        );
        coordinator.abandonUnusableSession(oauthService);
        coordinator.forceReauth(
          typeof window === 'undefined'
            ? undefined
            : window.location.pathname + window.location.search,
        );
      }
    });

    const discoveryUrl = betterAuthDiscoveryUrl(environment);
    coordinator.registerDiscoveryLoader(() =>
      oauthService.loadDiscoveryDocument(discoveryUrl),
    );

    // `setupAutomaticSilentRefresh()` subscribes to token events each call, so arm once.
    let silentRefreshArmed = false;
    const armSilentRefresh = () => {
      if (silentRefreshArmed) {
        return;
      }
      silentRefreshArmed = true;
      oauthService.setupAutomaticSilentRefresh();
    };
    const recoverExpiredSessionAtBoot = () => {
      coordinator.ensureValidToken().then(
        (refreshed) => {
          if (refreshed) {
            armSilentRefresh();
          }
          // `false` = refresh token rejected; the guard routes to login on navigation.
        },
        () => {
          // Issuer unreachable; the online/focus listener retries.
        },
      );
    };

    console.log('[oidc] loading discovery document', { discoveryUrl });
    return oauthService
      .loadDiscoveryDocument(discoveryUrl)
      .then(() => {
        console.log('[oidc] discovery document loaded', {
          loginUrl: oauthService.loginUrl,
          discoveryDocumentLoaded: oauthService.discoveryDocumentLoaded,
        });
        if (isPendingOidcAuthorizeRequestUrl()) {
          console.log(
            '[oidc] skipping tryLogin — URL is a pending authorize request (not a callback), preserving state/scope for LoginComponent',
          );
          return Promise.resolve(undefined);
        }
        return oauthService.tryLogin();
      })
      .then((tryLoginResult) => {
        console.log('[oidc] tryLogin resolved', {
          tryLoginResult,
          hasValidAccessToken: oauthService.hasValidAccessToken(),
          hasValidIdToken: oauthService.hasValidIdToken?.(),
        });
        if (oauthService.hasValidAccessToken()) {
          armSilentRefresh();
          registerActivityBasedRefresh(coordinator, armSilentRefresh);
        } else if (oauthService.getRefreshToken()) {
          // Access token already expired at boot (app was closed/asleep past expiry)
          // but the session is recoverable. Without this the Electron app never
          // registers any refresh trigger and sits on an expired token until restart.
          registerActivityBasedRefresh(coordinator, armSilentRefresh);
          recoverExpiredSessionAtBoot();
        }
      })
      .catch((err) => {
        // A discovery/tryLogin failure (issuer mismatch, auth server down, bad
        // network) must NOT reject here — a rejected APP_INITIALIZER aborts Angular
        // bootstrap and white-screens the whole app. Log it and let the app render;
        // JwtGuard then routes to /auth/login, where sign-in retries the flow.
        console.error(
          '[oidc] discovery/init failed — booting unauthenticated',
          err,
          {
            loginUrl: oauthService.loginUrl,
            discoveryDocumentLoaded: oauthService.discoveryDocumentLoaded,
          },
        );
        Sentry.captureException(
          err instanceof Error ? err : new Error(String(err)),
          { tags: { area: 'oidc-init' } },
        );
        // Offline cold boot: a stored refresh token is still good. Register the
        // triggers so the coordinator reloads discovery and refreshes once the
        // network returns, instead of leaving the app dead until restart.
        if (oauthService.getRefreshToken()) {
          registerActivityBasedRefresh(coordinator, armSilentRefresh);
        }
      });
  };
}
